import { IFiltersList } from "@/src/types/applicationOnline";
import {
  IApplicantAccountingCreatePayload,
  IApplicantAccountingListItem,
  IApplicantAccountingListResponse,
  IApplicantAccountingUpdatePayload,
  IApplicantInfoCreatePayload,
  IApplicantInfoDetail,
  IApplicantInfoListItem,
  IApplicantInfoPayload,
} from "@/src/types/officeAccounting";
import { applicantsRequests } from "@/src/api/applicantsRequests";
import { apiType } from "./apiType";

/** Response shape for GET /applicant-info/list and /applicant-info/search/filter. */
export interface IOfficeAccountingListResponse {
  data?: IApplicantInfoListItem[];
  count?: number;
}

const buildListParams = (limit: string, offset: number, office: string) => ({
  limit,
  offset,
  office,
});

const APPLICANT_ACCOUNTING_ALREADY_EXISTS = /applicant accounting already exists/i;

const parseApplicantAccountingApiError = (error: unknown): string => {
  const axiosError = error as { response?: { data?: { detail?: unknown } } };
  const detail = axiosError.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === "object" && item && "msg" in item ? String(item.msg) : JSON.stringify(item)))
      .join("; ");
  }
  if (detail != null && typeof detail === "object") {
    const record = detail as Record<string, unknown>;
    if (typeof record.message === "string") return record.message;
    if (typeof record.detail === "string") return record.detail;
    return JSON.stringify(detail);
  }
  if (detail != null) return JSON.stringify(detail);
  return "Could not create the Office Accounting record.";
};

const parsePositiveId = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
};

/** Reads accounting record id from applicant.accounting / create error payload. */
const parseAccountingId = (value: unknown): number | null => {
  const direct = parsePositiveId(value);
  if (direct != null) return direct;
  if (value == null || typeof value !== "object") return null;

  const record = value as Record<string, unknown>;
  for (const key of ["id", "accounting_id", "applicant_accounting_id"] as const) {
    const parsed = parsePositiveId(record[key]);
    if (parsed != null) return parsed;
  }
  return null;
};

const parseAccountingIdFromCreateError = (error: unknown): number | null => {
  const axiosError = error as { response?: { data?: unknown } };
  const data = axiosError.response?.data;
  if (data == null || typeof data !== "object") return null;

  const record = data as Record<string, unknown>;
  const fromRoot = parseAccountingId(record);
  if (fromRoot != null) return fromRoot;

  if (record.detail != null && typeof record.detail === "object") {
    return parseAccountingId(record.detail);
  }
  return null;
};

const resolveExistingAccountingId = async (applicantId: number, createError?: unknown): Promise<number | null> => {
  const fromError = parseAccountingIdFromCreateError(createError);
  if (fromError != null) return fromError;

  const applicant = await applicantsRequests.getById(applicantId);
  return parseAccountingId(applicant?.accounting);
};

const buildSemesterCostPatchFromCreatePayload = (
  data: IApplicantAccountingCreatePayload,
): Partial<IApplicantAccountingUpdatePayload> => ({
  ...(data.cost_first_semester !== undefined ? { cost_first_semester: data.cost_first_semester } : {}),
  ...(data.cost_second_semester !== undefined ? { cost_second_semester: data.cost_second_semester } : {}),
  ...(data.cost_third_semester !== undefined ? { cost_third_semester: data.cost_third_semester } : {}),
  ...(data.cost_fourth_semester !== undefined ? { cost_fourth_semester: data.cost_fourth_semester } : {}),
  ...(data.cost_fifth_semester !== undefined ? { cost_fifth_semester: data.cost_fifth_semester } : {}),
  ...(data.cost_sixth_semester !== undefined ? { cost_sixth_semester: data.cost_sixth_semester } : {}),
  ...(data.cost_seventh_semester !== undefined ? { cost_seventh_semester: data.cost_seventh_semester } : {}),
  ...(data.cost_eighth_semester !== undefined ? { cost_eighth_semester: data.cost_eighth_semester } : {}),
  ...(data.cost_ninth_semester !== undefined ? { cost_ninth_semester: data.cost_ninth_semester } : {}),
  ...(data.cost_tenth_semester !== undefined ? { cost_tenth_semester: data.cost_tenth_semester } : {}),
  ...(data.cost_eleventh_semester !== undefined ? { cost_eleventh_semester: data.cost_eleventh_semester } : {}),
  ...(data.cost_twelfth_semester !== undefined ? { cost_twelfth_semester: data.cost_twelfth_semester } : {}),
});

export type ApplicantAccountingApproveResult =
  | { ok: true; mode: "created" | "updated" | "existing" }
  | { ok: false; message: string };

/** GET /applicant-info/list — как Online applications (limit, offset, office). */
export const officeAccountingPageRequests = {
  getList: async (limit: string, offset: number, office: string) => {
    try {
      return await apiType.main.get<IOfficeAccountingListResponse>("/applicant-info/list", buildListParams(limit, offset, office));
    } catch (error) {
      console.log("Office Accounting getList error:", error);
      return null;
    }
  },

  /** GET /applicant-accounting/list */
  getApplicantAccountingList: async (limit: string, offset: number, office: string) => {
    try {
      return await apiType.main.get<IApplicantAccountingListResponse>(
        "/applicant-accounting/list",
        buildListParams(limit, offset, office),
      );
    } catch (error) {
      console.log("Office Accounting getApplicantAccountingList error:", error);
      return null;
    }
  },

  /** GET /applicant-accounting/filters/list — available filter options for the selected office. */
  getFilterList: async (office: string) => {
    try {
      const normalizedOffice = office.trim() || "ALL";
      return await apiType.main.get<IFiltersList>(`/applicant-accounting/filters/list?office=${encodeURIComponent(normalizedOffice)}`);
    } catch (error) {
      console.log("Office Accounting getFilterList error:", error);
      return null;
    }
  },

  /** POST /applicant-accounting/filters/apply — filtered & paginated table data. */
  applyFilters: async (limit: string, offset: number, office: string, data: object = {}) => {
    try {
      const normalizedOffice = office.trim() || "ALL";
      return await apiType.main.post<IApplicantAccountingListResponse>(
        `/applicant-accounting/filters/apply?office=${encodeURIComponent(normalizedOffice)}&limit=${limit}&offset=${offset}`,
        data,
      );
    } catch (error) {
      console.log("Office Accounting applyFilters error:", error);
      return null;
    }
  },

  /** GET /applicant-info/search/filter — query, limit, offset, office в query. */
  searchFilter: async (limit: string, offset: number, office: string, query: string) => {
    try {
      return await apiType.main.get<IOfficeAccountingListResponse>("/applicant-info/search/filter", {
        ...buildListParams(limit, offset, office),
        query,
      });
    } catch (error) {
      console.log("Office Accounting searchFilter error:", error);
      return null;
    }
  },

  getByStudentInfoId: async (studentInfoId: number | string) => {
    try {
      return await apiType.main.get<IApplicantInfoDetail>(`/applicant-info/${studentInfoId}`);
    } catch (error) {
      console.log("Office Accounting getByStudentInfoId error:", error);
      return null;
    }
  },

  /** Reserved — not used in UI yet. */
  create: async (data: IApplicantInfoCreatePayload) => {
    try {
      await apiType.main.post("/applicant-info/create", data);
      return true;
    } catch (error) {
      console.log("Office Accounting create error:", error);
      return false;
    }
  },

  /** POST /applicant-accounting/create — Document Check approve → Office Accounting row. */
  createApplicantAccounting: async (data: IApplicantAccountingCreatePayload): Promise<{ ok: true } | { ok: false; message: string }> => {
    try {
      await apiType.main.post("/applicant-accounting/create", data);
      return { ok: true };
    } catch (error) {
      const message = parseApplicantAccountingApiError(error);
      const axiosError = error as { response?: { data?: unknown } };
      console.log("Office Accounting createApplicantAccounting error:", axiosError.response?.data ?? error);
      return { ok: false, message };
    }
  },

  /**
   * Resolves existing accounting row id for an applicant.
   * Uses applicant.accounting.id — no list/filters lookup.
   */
  findApplicantAccountingByApplicantId: async (
    applicantId: number,
    _office?: string,
  ): Promise<IApplicantAccountingListItem | null> => {
    const accountingId = await resolveExistingAccountingId(applicantId);
    if (accountingId == null) return null;
    return { id: accountingId, applicant_id: applicantId };
  },

  /**
   * Create accounting row, or if it already exists —
   * resolve id from applicant and PUT /applicant-accounting/update.
   */
  createOrUpdateApplicantAccountingForApprove: async (
    data: IApplicantAccountingCreatePayload,
  ): Promise<ApplicantAccountingApproveResult> => {
    try {
      await apiType.main.post("/applicant-accounting/create", data);
      return { ok: true, mode: "created" };
    } catch (error) {
      const message = parseApplicantAccountingApiError(error);
      const axiosError = error as { response?: { data?: unknown } };
      console.log("Office Accounting createOrUpdateApplicantAccountingForApprove error:", axiosError.response?.data ?? error);

      if (!APPLICANT_ACCOUNTING_ALREADY_EXISTS.test(message)) {
        return { ok: false, message };
      }

      const accountingId = await resolveExistingAccountingId(data.applicant_id, error);
      if (accountingId == null) {
        return {
          ok: false,
          message:
            "Office Accounting already exists, but accounting id was not found on the applicant. Cannot update costs.",
        };
      }

      console.groupCollapsed("[Office Accounting] already exists → PUT /applicant-accounting/update");
      console.log("accounting id:", accountingId);
      console.log("applicant_id:", data.applicant_id);
      console.groupEnd();

      const updated = await officeAccountingPageRequests.updateApplicantAccounting(accountingId, {
        admission_no: data.admission_no,
        data_admission: data.data_admission,
        general_status: data.general_status,
        ...buildSemesterCostPatchFromCreatePayload(data),
      });

      return updated == null
        ? {
            ok: false,
            message: "Office Accounting already exists, but updating admission/cost fields failed.",
          }
        : { ok: true, mode: "updated" };
    }
  },

  update: async (studentInfoId: number | string, data: IApplicantInfoPayload) => {
    try {
      return await apiType.main.put<IApplicantInfoDetail>(`/applicant-info/update/${studentInfoId}`, data, {
        params: { student_info_id: studentInfoId },
      });
    } catch (error) {
      console.log("Office Accounting update error:", error);
      return null;
    }
  },

  /** PUT /applicant-accounting/update — id передаётся в теле запроса. */
  updateApplicantAccounting: async (accountingId: number | string, data: IApplicantAccountingUpdatePayload) => {
    try {
      const parsedId = Number(accountingId);
      return await apiType.main.put("/applicant-accounting/update", {
        id: Number.isFinite(parsedId) ? parsedId : accountingId,
        ...data,
      });
    } catch (error) {
      console.log("Office Accounting updateApplicantAccounting error:", error);
      return null;
    }
  },

  delete: async (studentInfoId: number | string) => {
    try {
      await apiType.main.delete(`/applicant-info/delete/${studentInfoId}`, undefined, {
        params: { student_info_id: studentInfoId },
      });
      return true;
    } catch (error) {
      console.log("Office Accounting delete error:", error);
      return false;
    }
  },
};
